import { pontoNoMundo } from './ancoragem'
import { parametrosDoObjeto } from './parametros'
import { REFERENCIAS_ALTURA } from './referenciasAltura'
import { alturaDoTerrenoEm } from './terreno'
import { apoioNoPonto } from './superficies'
import { calcularForcaDeChoque, type EntradaForcaDeChoque, type ResultadoForcaDeChoque } from './forcaDeChoque'
import { MATERIAIS_TALABARTE, materialDoIndice, type MaterialTalabarte } from './materiaisTalabarte'
import { dobraDoIndice, fatorNaAncoragem, redutorEfetivo, type DobraNoAlto, type RedutorEfetivo } from './redutoresForca'
import type { CenaTabletop, CordaTabletop, ObjetoTabletop, ReferenciaPonto, VetorTabletop } from './tipos'

/**
 * Queda em altura, didática: fator de queda, queda livre e zona livre de queda
 * (ZLQ) de um trabalhador preso por talabarte a um ponto de ancoragem.
 * TypeScript puro, com testes; a animação e a física, quando vierem, são
 * conferidas contra estes números.
 *
 * Modelo (queda vertical, sem pêndulo):
 *
 *   a   = altura da ancoragem sobre o engate (negativa se a ancoragem está abaixo)
 *   L   = comprimento do talabarte
 *   queda livre   h = L − a            (0 se a ≥ L)
 *   fator de queda  = h / L            (0 a 2)
 *   ZLQ = L + A + E + M                (medida da ancoragem para baixo)
 *
 * com A a abertura do absorvedor, E a distância do engate aos pés e M a margem.
 * A ZLQ não depende de onde o trabalhador estava: parado, ele fica pendurado
 * na vertical da ancoragem, com o engate a `L + A` abaixo dela e os pés `E`
 * abaixo do engate. O fator de queda, esse sim, depende de a.
 */

export interface EntradaQueda {
  readonly ancoragem: VetorTabletop
  readonly engate: VetorTabletop
  readonly comprimentoTalabarteM: number
  readonly absorvedorAberturaM: number
  readonly engateAosPesM: number
  readonly margemSegurancaM: number
  /** A superfície mais baixa em que o trabalhador poderia cair (altura Y). */
  readonly superficieAbaixoY: number
}

export interface ResultadoQueda {
  /** Distância em linha reta do engate à ancoragem. */
  readonly distanciaM: number
  /** O talabarte alcança a ancoragem a partir de onde o trabalhador está. */
  readonly alcanca: boolean
  /** Altura da ancoragem sobre o engate (negativa: ancoragem abaixo do engate). */
  readonly ancoragemSobreEngateM: number
  readonly quedaLivreM: number
  readonly fatorDeQueda: number
  readonly parcelasZlq: {
    readonly talabarteM: number
    readonly absorvedorM: number
    readonly engateAosPesM: number
    readonly margemM: number
  }
  readonly zonaLivreM: number
  /** Da ancoragem até a superfície mais baixa abaixo dela. */
  readonly alturaLivreM: number
  /** `alturaLivre − ZLQ`: negativa é o trabalhador bater antes de o sistema parar a queda. */
  readonly folgaM: number
  readonly baterNoChao: boolean
  /** Altura final dos pés, parado e pendurado na vertical da ancoragem. */
  readonly pesFinaisY: number
  /** Há queda livre acima do limite e nenhum absorvedor de energia. */
  readonly semAbsorvedorComQuedaLivreAlta: boolean
  /** O fator passa do alerta (ancoragem abaixo do engate). */
  readonly fatorDesfavoravel: boolean
}

const TOLERANCIA_ALCANCE_M = 1e-6

export function analisarQueda(e: EntradaQueda): ResultadoQueda {
  const L = e.comprimentoTalabarteM
  const distanciaM = Math.hypot(e.ancoragem[0] - e.engate[0], e.ancoragem[1] - e.engate[1], e.ancoragem[2] - e.engate[2])
  const a = e.ancoragem[1] - e.engate[1]
  const quedaLivreM = a >= L ? 0 : L - a
  const fatorDeQueda = L > 0 ? Math.min(2, quedaLivreM / L) : 0
  const zonaLivreM = L + e.absorvedorAberturaM + e.engateAosPesM + e.margemSegurancaM
  const alturaLivreM = e.ancoragem[1] - e.superficieAbaixoY
  const folgaM = alturaLivreM - zonaLivreM
  return {
    distanciaM,
    alcanca: distanciaM <= L + TOLERANCIA_ALCANCE_M,
    ancoragemSobreEngateM: a,
    quedaLivreM,
    fatorDeQueda,
    parcelasZlq: {
      talabarteM: L,
      absorvedorM: e.absorvedorAberturaM,
      engateAosPesM: e.engateAosPesM,
      margemM: e.margemSegurancaM,
    },
    zonaLivreM,
    alturaLivreM,
    folgaM,
    baterNoChao: folgaM < 0,
    pesFinaisY: e.ancoragem[1] - L - e.absorvedorAberturaM - e.engateAosPesM,
    semAbsorvedorComQuedaLivreAlta:
      e.absorvedorAberturaM <= 0 && quedaLivreM > REFERENCIAS_ALTURA.quedaLivreSemAbsorvedorM.valor,
    fatorDesfavoravel: fatorDeQueda > REFERENCIAS_ALTURA.fatorDeQuedaDeAlerta.valor,
  }
}

// ---------------------------------------------------------------------------
// Da cena ao cálculo
// ---------------------------------------------------------------------------

export interface TalabarteAnalisado {
  readonly idCorda: string
  readonly idTrabalhador: string
  readonly ancoragem: ReferenciaPonto
  /** Ponto do mundo da ancoragem, para desenhar a zona livre. */
  readonly pontoAncoragem: VetorTabletop
  /** A superfície mais baixa considerada, para desenhar onde a queda acabaria. */
  readonly superficieAbaixoY: number
  readonly resultado: ResultadoQueda
  readonly choque: ChoqueDoTalabarte
}

/** A força de choque da queda deste talabarte, com o material e a massa do trabalhador. */
export interface ChoqueDoTalabarte {
  readonly redutor: RedutorEfetivo
  readonly dobra: DobraNoAlto
  /** Quantas vezes a força do corpo chega à ancoragem: 1 com o talabarte direto, até 2 com a corda dobrada no alto. */
  readonly fatorNaAncoragem: number
  /** O pico de força que a ancoragem recebe. */
  readonly forcaNaAncoragemKN: number
  readonly material: MaterialTalabarte
  readonly massaKg: number
  readonly resultado: ResultadoForcaDeChoque
  /** O pico passa da força máxima de referência no corpo. */
  readonly acimaDoLimiteDoCorpo: boolean
  /**
   * Quanto a descida depois de o talabarte esticar (alongamento + abertura)
   * passa da abertura do absorvedor que a zona livre de queda considera.
   * Positivo: a zona livre, como está, subestima a queda.
   */
  readonly alemDaZonaLivreM: number
}

/** A entrada do cálculo da força de choque, com os parâmetros do trabalhador e a queda livre dada. */
export function entradaDeChoque(trabalhador: Pick<ObjetoTabletop, 'tipo' | 'parametros'>, quedaLivreM: number): EntradaForcaDeChoque {
  const p = parametrosDoObjeto(trabalhador)
  const redutor = redutorEfetivo(p)
  return {
    massaKg: p.massaKg!,
    quedaLivreM,
    comprimentoM: p.comprimentoTalabarteM!,
    rigidezEAkN: MATERIAIS_TALABARTE[materialDoIndice(p.materialTalabarte!)].rigidezEAkN,
    forcaAbsorvedorKN: redutor.forcaKN,
    aberturaMaximaAbsorvedorM: redutor.aberturaM,
  }
}

/** A força de choque de um talabarte, a partir da queda livre e dos parâmetros do trabalhador. */
export function choqueDoTalabarte(trabalhador: Pick<ObjetoTabletop, 'tipo' | 'parametros'>, quedaLivreM: number): ChoqueDoTalabarte {
  const p = parametrosDoObjeto(trabalhador)
  const material = materialDoIndice(p.materialTalabarte!)
  const redutor = redutorEfetivo(p)
  const aberturaMaxima = redutor.aberturaM
  const dobra = dobraDoIndice(p.dobraNoAlto!)
  const fator = fatorNaAncoragem(dobra)
  const resultado = calcularForcaDeChoque(entradaDeChoque(trabalhador, quedaLivreM))
  return {
    redutor,
    dobra,
    fatorNaAncoragem: fator,
    forcaNaAncoragemKN: resultado.forcaPicoKN * fator,
    material,
    massaKg: p.massaKg!,
    resultado,
    acimaDoLimiteDoCorpo: resultado.forcaPicoKN > REFERENCIAS_ALTURA.forcaMaximaNoCorpoKN.valor,
    alemDaZonaLivreM: resultado.descidaAposEsticarM - aberturaMaxima,
  }
}

const ehPonto = (referencia: ReferenciaPonto, objeto: ObjetoTabletop | undefined, idPonto: string) =>
  objeto !== undefined && referencia.idObjeto === objeto.id && referencia.idPonto === idPonto

/** Quantos pontos em cada anel ao redor da ancoragem, na busca da superfície mais baixa. */
const PONTOS_POR_ANEL = 16

/**
 * A superfície mais baixa em que o trabalhador poderia cair: a menor altura do
 * terreno dentro do alcance horizontal do talabarte em volta da ancoragem. É o
 * pior caso: o trabalhador pode estar na borda e cair para o lado de baixo.
 */
export function superficieMaisBaixaAoRedor(cena: CenaTabletop, centro: VetorTabletop, alcanceM: number): number {
  let menor = alturaDoTerrenoEm(cena, centro[0], centro[2])
  for (const fracao of [0.5, 1]) {
    const raio = alcanceM * fracao
    if (raio <= 0) continue
    for (let i = 0; i < PONTOS_POR_ANEL; i += 1) {
      const angulo = (2 * Math.PI * i) / PONTOS_POR_ANEL
      menor = Math.min(menor, alturaDoTerrenoEm(cena, centro[0] + raio * Math.cos(angulo), centro[2] + raio * Math.sin(angulo)))
    }
  }
  return menor
}

/**
 * A superfície em que a queda acabaria: entre os pontos do alcance em que o
 * trabalhador fica no vazio (sem apoio no nível dos pés), a superfície mais
 * ALTA abaixo dele, que é o primeiro obstáculo. Numa laje é o chão além da
 * borda; dentro de uma torre, o patamar de baixo, pelo alçapão. Se não há
 * vazio ao alcance (ele não tem por onde cair), fica a superfície mais baixa
 * do alcance, como antes.
 */
export function superficieDeQuedaAoRedor(cena: CenaTabletop, centro: VetorTabletop, alcanceM: number, yPes: number): number {
  let maisAlta: number | null = null
  const pontos: Array<readonly [number, number]> = [[centro[0], centro[2]]]
  for (const fracao of [0.5, 1]) {
    const raio = alcanceM * fracao
    if (raio <= 0) continue
    for (let i = 0; i < PONTOS_POR_ANEL; i += 1) {
      const angulo = (2 * Math.PI * i) / PONTOS_POR_ANEL
      pontos.push([centro[0] + raio * Math.cos(angulo), centro[2] + raio * Math.sin(angulo)])
    }
  }
  for (const [x, z] of pontos) {
    const { apoiado, superficie } = apoioNoPonto(cena, x, z, yPes)
    if (!apoiado) maisAlta = maisAlta === null ? superficie.altura : Math.max(maisAlta, superficie.altura)
  }
  return maisAlta ?? superficieMaisBaixaAoRedor(cena, centro, alcanceM)
}

function extremos(corda: CordaTabletop, cena: CenaTabletop): { trabalhador: ObjetoTabletop; ancoragem: ReferenciaPonto } | null {
  if (corda.percurso.length < 2) return null
  const primeiro = corda.percurso[0]!
  const ultimo = corda.percurso[corda.percurso.length - 1]!
  for (const [engate, outro] of [[primeiro, ultimo], [ultimo, primeiro]] as const) {
    const trabalhador = cena.objetos.find((o) => o.tipo === 'trabalhador' && ehPonto(engate, o, 'engate-dorsal'))
    if (trabalhador) return { trabalhador, ancoragem: outro }
  }
  return null
}

/**
 * Analisa cada talabarte da cena: a corda de função `talabarte` que sai do
 * engate dorsal de um trabalhador e chega a um ponto de ancoragem.
 */
export function analisarTalabartes(cena: CenaTabletop): readonly TalabarteAnalisado[] {
  const analisados: TalabarteAnalisado[] = []
  for (const corda of cena.cordas ?? []) {
    if (corda.funcao !== 'talabarte') continue
    const pontas = extremos(corda, cena)
    if (!pontas) continue
    const objetoAncoragem = cena.objetos.find((o) => o.id === pontas.ancoragem.idObjeto)
    if (!objetoAncoragem) continue
    let pontoAncoragem: VetorTabletop
    try {
      pontoAncoragem = pontoNoMundo(objetoAncoragem, pontas.ancoragem.idPonto)
    } catch {
      continue
    }
    const { trabalhador } = pontas
    const engate = pontoNoMundo(trabalhador, 'engate-dorsal')
    const p = parametrosDoObjeto(trabalhador)
    const L = p.comprimentoTalabarteM!
    const a = pontoAncoragem[1] - engate[1]
    // alcance horizontal do trabalhador em torno da ancoragem: L na altura dela, menos se ela está acima
    const alcanceM = Math.sqrt(Math.max(0, L * L - Math.max(0, a) ** 2))
    const superficieAbaixoY = superficieDeQuedaAoRedor(cena, pontoAncoragem, alcanceM, trabalhador.transformacao.posicao[1])
    const resultado = analisarQueda({
      ancoragem: pontoAncoragem,
      engate,
      comprimentoTalabarteM: L,
      absorvedorAberturaM: redutorEfetivo(p).aberturaM,
      engateAosPesM: p.engateAosPesM!,
      margemSegurancaM: p.margemSegurancaM!,
      superficieAbaixoY,
    })
    analisados.push({
      idCorda: corda.id,
      idTrabalhador: trabalhador.id,
      ancoragem: pontas.ancoragem,
      pontoAncoragem,
      superficieAbaixoY,
      resultado,
      choque: choqueDoTalabarte(trabalhador, resultado.quedaLivreM),
    })
  }
  return analisados
}
